import { useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Breadcrumb from '../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../utils/seo';

export default function ButlerPage() {
  useEffect(() => {
    updateSEO(seoData['/butler']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '執事とは' }
  ];

  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const responsibilityAreas = [
    { no: '01', en: 'Household Continuity', ja: '家庭運営の継続性', desc: '日々の段取り、来客対応、備品・在庫の把握、外部業者との調整など、暮らしが滞りなく回るための基盤を整えます。単発の作業ではなく、家庭全体のリズムを見渡す視点が求められます。' },
    { no: '02', en: 'Discretion & Trust', ja: '機密と信頼の保持', desc: '家庭の内側に入る職である以上、見聞きした事柄への沈黙と、判断の慎重さが不可欠です。信頼は技能の前提条件であり、到達水準の評価においても中核を占めます。' },
    { no: '03', en: 'Anticipatory Service', ja: '先回りの察知と手配', desc: '言葉にされる前のニーズを読み、過不足のない手配へつなげる力です。目立たず、しかし確かに支える「見えない奉仕」を、当協会は執事の本質的な責任と位置づけています。' },
    { no: '04', en: 'Protocol & Etiquette', ja: '礼法と対人プロトコル', desc: '来客・式典・会食など、場に応じた作法と進行管理を担います。西洋式の規律と日本の礼節を状況に応じて使い分ける判断力が、職能の深みを示します。' },
  ];

  const boundaries = [
    { role: '家政婦・家事代行', en: 'Housekeeping', point: '作業の実行が主座', text: '掃除・洗濯・調理といった個別の家事業務を確実に遂行する職能です。執事も家事に関与し得ますが、主眼は「何を・どの順で・誰が担うか」を設計し、家庭全体の品質を保つことにあります。現場の手を動かすことと、家庭運営を統括することは、役割の層が異なります。' },
    { role: '秘書', en: 'Secretary', point: '業務と予定の管理が主座', text: '秘書は、主人の仕事上の予定・文書・連絡を中心に支えます。執事はプライベート側の生活基盤に重心を置き、家庭・来客・移動・身辺の整えまでを視野に入れます。重なる部分はあっても、責任の主戦場が「オフィス」か「暮らし」かで職能は分かれます。' },
    { role: 'コンシェルジュ', en: 'Concierge', point: '依頼への応答と手配が主座', text: 'コンシェルジュは、寄せられた要望に対して最適な情報と手配を返す応答型の専門性に長けます。執事は、日々の観察に基づく先回りと、長期的な信頼関係のなかで家庭の文脈を蓄積していく継続型の専門性を核とします。単発の手配力と、生活に伴走する統括力は補完関係にあります。' },
  ];

  const levels = [
    {
      tier: 'I',
      name: 'Associate Butler',
      ja: 'アソシエイト・バトラー',
      href: '/qualifications/associate-butler',
      img: '/images/generated/jba-become-foundation-training',
      alt: 'アソシエイト・バトラー段階の基礎研修。礼法と奉仕の基本を学ぶ場面',
      lead: '基礎となる姿勢・礼法・観察眼を身につける入門の到達点',
      text: '執事としての立ち居振る舞い、言葉遣い、機密保持の意識、基本的な奉仕の手順を体系的に学びます。現場に入る前に「何を大切にする職か」を身体と意識の両面で整え、以降の専門性を積み上げる土台を築く段階です。',
    },
    {
      tier: 'II',
      name: 'Professional Butler',
      ja: 'プロフェッショナル・バトラー',
      href: '/qualifications/professional-butler',
      img: '/images/generated/jba-become-professional-practice',
      alt: 'プロフェッショナル・バトラー段階の実務研鑽。現場を想定した実践の様子',
      lead: '家庭運営を実務として回せる、一人前の職能水準',
      text: '来客対応、スケジュール調整、業者管理、食卓や空間の整えなど、実務の幅を広げ、状況判断のもとでサービスを設計・実行できる水準です。主人の意向をくみ取り、過不足なく形にする再現性が求められます。',
    },
    {
      tier: 'III',
      name: 'Master Butler',
      ja: 'マスター・バトラー',
      href: '/qualifications/master-butler',
      img: '/images/generated/jba-become-master-leadership',
      alt: 'マスター・バトラー段階のリーダーシップ研鑽。後進指導と統括の姿勢',
      lead: '統括・育成・品質の最終責任を担う熟達の水準',
      text: '複数のスタッフや外部協力者を束ね、家庭・邸宅のサービス品質を統括する段階です。後進の指導、難易度の高い局面での意思決定、長く続く信頼関係の維持など、職能の深部と広部の双方が問われます。',
    },
  ];

  const misconceptions = [
    { q: '執事は富裕層だけの、遠い存在ではないのか', a: '映像作品や小説が描く華麗なイメージは一面にすぎません。当協会が定義する執事は、暮らしの基盤を整え、主人が本来集中すべき事柄へ向かえるよう支える職能です。規模の大小より、責任の持ち方と到達水準の明確さが本質だと考えます。' },
    { q: '家事代行や秘書と同じではなのか', a: '重なる技能はありますが、責任の主座が異なります。家事代行は個別作業の確実な遂行、秘書は業務側の支援、執事は家庭運営全体の継続性と先回りを中核に据えます。境界を曖昧にしたままでは、依頼側も担い手側も期待値を合わせにくくなります。' },
    { q: '資格がなくても実務経験があれば十分では', a: '経験は尊いものですが、属人的な勘だけに頼ると品質の再現が難しくなります。当協会の到達水準は、技能と姿勢を第三者にも説明可能な形で示すための枠組みです。経験を言語化し、次の段階へ進む道標として資格制度を運用しています。' },
    { q: '西洋の伝統を真似るだけの文化ではないのか', a: '当協会は1890年（明治23年）の創設以来、英国式の規律と日本の礼節・おもてなしを融合させる道を歩んできました。輸入ではなく、日本の生活文脈に根ざした職能として執事を定義し、育成と認定を続けています。' },
  ];

  const faqJsonLd = [
    {
      question: '全日本執事協会は執事をどのように定義していますか',
      answer: '当協会は執事を、家庭および主人の生活基盤を統括的に支え、先回りの察知と機密保持を核とする専門職能と定義します。個別の家事作業の代行にとどまらず、暮らし全体の継続性と品格を設計・維持する役割を担います。',
    },
    {
      question: '家政婦・秘書・コンシェルジュと執事の違いは何ですか',
      answer: '家政婦・家事代行は個別家事業務の遂行、秘書は業務上の予定や文書の管理、コンシェルジュは依頼への応答と手配を主座とします。執事は家庭運営の統括と先回り、長期的な信頼関係に基づく伴走を中核とし、責任の層が異なります。',
    },
    {
      question: '資格の到達水準にはどのような段階がありますか',
      answer: 'アソシエイト・バトラー、プロフェッショナル・バトラー、マスター・バトラーの三段階に加え、食事・旅行・ビジネスマナーなどの専門資格を整備しています。基礎の姿勢から実務、統括・育成まで、段階的に職能を証明できる構成です。',
    },
    {
      question: '日本に執事という職は根づいているのですか',
      answer: '明治期の導入以来、社会の変化とともに形を変えながら継承されてきました。当協会は育成・認定・情報発信を通じて職能の定義を社会と共有し、誤解を整理しながら日本における執事職の現在地を更新し続けています。',
    },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事とは｜職能としての定義と到達水準',
    description:
      '一般社団法人全日本執事協会が、執事という職能をどう定義するか。責任の範囲、近接職との境界、Associate・Professional・Masterの到達水準、日本における現在地と誤解の整理。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会',
      foundingDate: '1890',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '青海2-7-4',
        addressLocality: '江東区',
        addressRegion: '東京都',
        addressCountry: 'JP',
      },
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会',
      identifier: '2010005039584',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/butler',
    },
    inLanguage: 'ja-JP',
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '執事とは', item: 'https://japanbutlerassociation.com/butler' },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqJsonLd.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード型 */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <picture>
          <source srcSet="/images/generated/jba-butler-profession-hero.webp" type="image/webp" />
          <img
            src="/images/generated/jba-butler-profession-hero.jpg"
            alt="執事という職能の象徴。静謐な空間で品格ある奉仕の姿勢を示す場面"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </picture>
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              The Butler Profession
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事とは</span>
              <span className="block text-[#d7b779]">職能としての定義</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、認定団体として「執事」を単なる家事の担い手ではなく、家庭と主人の生活基盤を統括的に支える専門職能と定義します。本稿では、責任の範囲、近接する職能との境界、協会が定める到達水準、そして日本における現在地を整理します。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-16 sm:space-y-20">

          {/* S1. 職能の定義 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Definition</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              執事という<span className="text-[#a38e65]">職能の定義</span>
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                当協会が用いる「執事」という言葉は、映像や小説が想起させる衣装や格式のイメージを出発点にはしません。中心にあるのは、主人と家庭の内側に入り、暮らしと対人関係の基盤を静かに整え続けるという職能上の責任です。個別の作業をこなすこと自体が目的ではなく、作業の背後にある優先順位・品位・継続性を設計し、維持することに主眼があります。
              </p>
              <p>
                ヨーロッパに起源を持つ奉仕の規律と、日本古来の「おもてなし」が重なる地点に、協会は執事の姿を置いています。先に言われる前に動くこと、過剰に主張せず必要な水準を保つこと、見聞きした事柄を外部へ漏らさないこと。これらは技能であると同時に、倫理でもあります。
              </p>
              <p>
                したがって当協会の定義では、執事は「便利な人手」ではありません。家庭という小さな社会の運営品質に責任を持ち、主人の時間と注意力を本来向かうべき対象へ戻すための専門職です。依頼の受け方や料金体系といった利用面の話は、グループのサービス案内に委ね、ここでは職能そのものの輪郭だけを扱います。
              </p>
            </div>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">視点</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">認定団体</dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">焦点</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">職能定義</dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">到達</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">三段階</dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">系譜</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">1890<span className="text-sm text-[#8f7a56] ml-1">年</span></dd>
              </div>
            </dl>
          </section>

          {/* S2. 責任の範囲 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. Scope of Responsibility</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              担う<span className="text-[#a38e65]">責任の範囲</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-9">
              執事の責任は、目に見える作業の完了報告だけでは測れません。家庭が翌日も、翌月も、同じ品位で回り続ける状態をつくること。そのために、次の四領域を協会は職能上の中核とみなします。
            </p>
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {responsibilityAreas.map((f) => (
                <div key={f.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{f.no}</span>
                  <div>
                    <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1">{f.en}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-2.5 leading-[1.5]">{f.ja}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-10 grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  責任の外縁もまた重要です。医療行為や法的代理、主人の意思決定そのものの代行は、執事の職能には含まれません。あくまで生活と対人サービスの領域で、主人の意思を前提に環境を整える立場です。境界を自覚していること自体が、プロフェッショナルの条件だと当協会は考えます。
                </p>
                <p>
                  また、責任は個人の気概だけに依存させません。手順の標準化、振り返りの習慣、上位水準者による指導といった仕組みがあって初めて、品質は属人的な「気働き」から職能へと昇華します。協会の研修と認定は、その仕組みを社会側にも可視化するための装置です。
                </p>
              </div>
              <div className="lg:pt-1">
                <picture>
                  <source srcSet="/images/generated/jba-butler-team-briefing.webp" type="image/webp" />
                  <img
                    src="/images/generated/jba-butler-team-briefing.jpg"
                    alt="執事チームのブリーフィング。責任分担と当日の段取りを確認する様子"
                    className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                  />
                </picture>
              </div>
            </div>
          </section>

          {/* S3. 職能上の境界 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. Professional Boundaries</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              近接職能との<span className="text-[#a38e65]">境界</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-9">
              家政婦・家事代行、秘書、コンシェルジュは、いずれも高い専門性を持つ職です。執事との違いは優劣ではなく、責任の主座と時間の持ち方にあります。境界を言語化することで、育成側も社会側も期待値を合わせやすくなります。
            </p>
            <div className="space-y-5">
              {boundaries.map((b) => (
                <div key={b.role} className="bg-white border border-[#e2ddd2] p-6 sm:p-8 shadow-[0_1px_0_rgba(26,41,66,0.04)]">
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3">
                    <h3 className="jba-display font-medium text-[#1a2942] text-xl leading-[1.5]">{b.role}</h3>
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em]">{b.en}</span>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-6 h-px bg-[#a38e65]"></span>
                    <span className="font-['Noto_Serif_JP',serif] text-sm text-[#a38e65] tracking-[0.04em]">{b.point}</span>
                  </div>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{b.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-10 grid lg:grid-cols-[320px_1fr] gap-8 lg:gap-12 items-start">
              <picture>
                <source srcSet="/images/generated/jba-become-foundation-training.webp" type="image/webp" />
                <img
                  src="/images/generated/jba-become-foundation-training.jpg"
                  alt="職能の境界を踏まえた基礎研修。礼法と役割分担の基本を身につける場面"
                  className="w-full h-56 lg:h-[320px] object-cover border border-[#a38e65]/30"
                />
              </picture>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] lg:pt-1">
                実務の現場では、一人が複数の機能を兼ねることもあります。それでも協会の教育と認定では、どこまでが執事固有の判断領域かを意識的に分けて伝えます。兼務は現実、混同は定義の放棄——この区別が、職能を守る第一歩です。
              </p>
            </div>
          </section>

          {/* S4. 到達水準（紺帯番号バンド） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Standards of Attainment</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              協会が定める<span className="text-[#a38e65]">到達水準</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-10">
              当協会は、アソシエイト・プロフェッショナル・マスターの三段階を、執事職能の到達水準として運用しています。加えて、食事・旅行・ビジネスマナーなどの専門資格により、特定領域の深さを証明できる構成です。以下は各段階が「何を担える状態か」を示す定義上の整理であり、利用申込や料金の案内ではありません。
            </p>

            <div className="space-y-0">
              {levels.map((lv, idx) => (
                <article key={lv.tier} className="border-t border-[#e2ddd2] first:border-t-0">
                  {/* 紺帯番号バンド */}
                  <div className="bg-[#1a2942] px-5 sm:px-7 py-4 flex flex-wrap items-center gap-x-5 gap-y-2">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl sm:text-3xl leading-none tracking-[0.06em]">{lv.tier}</span>
                    <div className="w-px h-6 bg-[#a38e65]/50 hidden sm:block"></div>
                    <div>
                      <span className="block font-['Playfair_Display',serif] italic text-[#a38e65] text-[12px] tracking-[0.12em]">{lv.name}</span>
                      <h3 className="jba-display font-medium text-[#f8f6f0] text-lg sm:text-xl leading-[1.4]">{lv.ja}</h3>
                    </div>
                  </div>
                  <div className={`grid lg:grid-cols-2 gap-0 ${idx % 2 === 1 ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                    <div className="p-6 sm:p-8 lg:p-10 flex flex-col justify-center bg-[#f8f6f0]/40">
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#a38e65] tracking-[0.04em] mb-3">{lv.lead}</p>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] mb-6">{lv.text}</p>
                      <a href={lv.href} className={`${BTN08_ON_LIGHT} self-start`}>水準の詳細を見る</a>
                    </div>
                    <div className="min-h-[240px] lg:min-h-[320px]">
                      <picture>
                        <source srcSet={`${lv.img}.webp`} type="image/webp" />
                        <img
                          src={`${lv.img}.jpg`}
                          alt={lv.alt}
                          className="w-full h-full min-h-[240px] lg:min-h-[320px] object-cover"
                        />
                      </picture>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-10 border border-[#e2ddd2] bg-white p-6 sm:p-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">
                三段階の全体像と専門資格の位置づけは、
                <a href="/qualifications" className="text-[#1a2942] border-b border-[#a38e65] hover:text-[#a38e65] transition-colors mx-1">資格制度の一覧</a>
                にまとめています。各水準は「称号」ではなく、観察・判断・実行・統括といった行為の質を段階的に示すための共通言語です。
              </p>
            </div>
          </section>

          {/* S5. 品質と研鑽の思想 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Quality Discipline</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              品質を<span className="text-[#a38e65]">職能として保つ</span>ということ
            </h2>
            <div className="grid lg:grid-cols-[320px_1fr] gap-8 lg:gap-12 items-start">
              <picture>
                <source srcSet="/images/generated/jba-butler-quality-control.webp" type="image/webp" />
                <img
                  src="/images/generated/jba-butler-quality-control.jpg"
                  alt="執事サービスの品質管理。細部の確認と標準の徹底を示す場面"
                  className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/30"
                />
              </picture>
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  到達水準を設ける理由は、試験のための試験を増やすことではありません。家庭の内側という、外部から検証しにくい領域だからこそ、担い手自身が「いま自分はどの層の判断まで任されているか」を冷静に把握する必要がある——そのための物差しです。
                </p>
                <p>
                  品質管理は、チェックリストの消化に終わりません。同じ手順でも、主人の体調や来客の性格、季節の空気で最適解は変わります。標準を身につけたうえで、標準から意図的に外す判断ができること。協会が育成で重視するのは、この往復運動です。
                </p>
                <p>
                  代表理事・石井裕一（三代目頭取）のもと、協会は伝統の継承と、現代の生活様式への適合を並行して進めています。所在地は東京都江東区青海2-7-4。法人番号2010005039584。物理的な研鑽の場を持ち、定義と実務を切り離さない運営を続けています。
                </p>
              </div>
            </div>
          </section>

          {/* S6. 日本における現在地 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. In Japan Today</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              日本における<span className="text-[#a38e65]">執事職の現在地</span>
            </h2>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 lg:gap-12 items-start">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  1890年（明治23年）の協会創設以来、日本の執事職は社会の変化とともに形を変えてきました。大邸宅に常駐する古典的な姿だけが唯一の正解ではなく、現代では家庭の規模やライフスタイルに応じて、統括の仕方や関与の密度が多様化しています。重要なのは外見の格式ではなく、責任の取り方が職能として説明可能かどうかです。
                </p>
                <p>
                  一方で、呼称だけが先行し、中身の定義が共有されないまま「執事風」のサービスが語られる場面も少なくありません。当協会は認定団体として、育成カリキュラムと到達水準を通じて言葉の意味を固定し、学び手が同じ地図を参照できるようにすることを使命の一つに置いています。
                </p>
                <p>
                  日本の礼節や季節感、住まいの構造は、西洋の古典的な屋敷運営とは条件が異なります。だからこそ、輸入された用語をそのまま置くのではなく、日本の生活文脈で機能する職能へ翻訳し続ける作業が欠かせません。協会概要に示す事業——人材育成、資格認定、セミナー、国際交流、出版・情報発信——は、いずれもこの翻訳と定着のための柱です。
                </p>
                <p>
                  現在地を一言でいえば、「認知は広がりつつあるが、定義の共有はまだ途上」です。だからこそ、依頼者目線の解説とは別に、認定側から職能の輪郭を示し続けるページが必要になります。本頁はその役割を担います。
                </p>
              </div>
              <div className="lg:pt-1">
                <picture>
                  <source srcSet="/images/generated/jba-become-professional-practice.webp" type="image/webp" />
                  <img
                    src="/images/generated/jba-become-professional-practice.jpg"
                    alt="日本の生活文脈に根ざした執事の実務。現代の家庭運営を支える専門性の場面"
                    className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/30"
                  />
                </picture>
              </div>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a href="/about/overview" className={BTN08_ON_LIGHT}>協会概要を読む</a>
              <a href="/butler-services" className={BTN08_ON_LIGHT}>執事サービスの考え方</a>
            </div>
          </section>

          {/* S7. よくある誤解 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VII. Misconceptions</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある<span className="text-[#a38e65]">誤解の整理</span>
            </h2>
            <div className="space-y-0 border-t-2 border-[#1a2942]">
              {misconceptions.map((m, i) => (
                <div key={i} className="border-b border-[#e2ddd2] py-6 sm:py-7">
                  <div className="flex gap-4 sm:gap-6">
                    <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 pt-0.5">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div>
                      <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3 leading-[1.5]">{m.q}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95]">{m.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* S8. FAQ（可視FAQ・JSON-LDと対応） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VIII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              職能定義に関する<span className="text-[#a38e65]">問いと答え</span>
            </h2>
            <div className="space-y-4">
              {faqJsonLd.map((item) => (
                <details key={item.question} className="group border border-[#e2ddd2] bg-white open:bg-[#f8f6f0]/35 transition-colors">
                  <summary className="cursor-pointer list-none flex items-start gap-4 p-5 sm:p-6 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.7]">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] shrink-0 mt-0.5">Q</span>
                    <span className="flex-1">{item.question}</span>
                    <span className="text-[#a38e65] shrink-0 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 pl-12 sm:pl-14">
                    <p className="font-['Noto_Serif_JP',serif] text-sm sm:text-[15px] text-[#4a4638] leading-[1.95]">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* S9. CTA */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IX. Next Steps</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              定義の先へ進む
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                執事を職能として理解したうえで、到達水準の詳細や協会の成り立ち、サービスという実装面へ進むことができます。言葉の定義と、育成・認定の現場は一続きです。関心のある入口から、続きをお読みください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 flex-wrap">
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度を見る</a>
                <a href="/qualifications/associate-butler" className={BTN08_ON_NAVY}>Associate を見る</a>
                <a href="/about/overview" className={BTN08_ON_NAVY}>協会概要</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
